"use client";

import { useStrictLocalization } from "@faire/localization/react/useStrictLocalization";
import { cn } from "@faire/react/tools/cn";
import { Dimensions } from "@faire/slate/foundation/dimensions";
import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { ChevronUp } from "@faire/slate-icons/ChevronUp";
import { Close } from "@faire/slate-icons/Close";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import { FloatingTrigger } from "@faire/slate/private/floating/FloatingTrigger";
import React from "react";

import { IFilterMenuWebProps } from "./types";

type IProps = {
  applied: boolean;
  "aria-controls"?: string;
  disabled: boolean;
  label: string;
  onClear?: () => void;
  onClick?: () => void;
} & IFilterMenuWebProps;

export const FilterPopupChip: React.FC<IProps> = ({
  applied,
  "aria-controls": ariaControls,
  disabled,
  label,
  onClear,
  ref,
  onClick,
  ...props
}) => {
  const { open, setOpen } = useFloatingContext();
  const { strictLocalize } = useStrictLocalization();

  const TriggerIcon = open ? ChevronUp : ChevronDown;

  const clearTriggerAriaLabel = strictLocalize({
    id: "ifas8Z",
    defaultMessage: "Clear filter",
    description: {
      text: "Accessible label for the X icon button on an applied filter chip",
    },
  });

  const triggerCls = triggerStyle.cls({
    applied,
    active: open,
  });
  const clearTriggerCls = clearTriggerStyle.cls({});

  return (
    <div {...props} className={cn("fs-filter-chip", componentCls())} ref={ref}>
      <FloatingTrigger>
        <button
          aria-controls={ariaControls}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={triggerCls}
          disabled={disabled}
          type="button"
          onClick={onClick}
        >
          <span className="fs-filter__label">{label}</span>
          {!applied ? (
            <span className="fs-filter__icon">
              <TriggerIcon
                aria-hidden
                color="inherit"
                fontSize={Dimensions[3]}
                titleAccess={undefined}
              />
            </span>
          ) : null}
        </button>
      </FloatingTrigger>
      {applied ? (
        <button
          aria-label={clearTriggerAriaLabel}
          className={clearTriggerCls}
          disabled={disabled}
          type="button"
          onClick={() => {
            onClear?.();
            setOpen(false);
          }}
        >
          <span className="fs-filter__icon">
            <Close
              aria-hidden
              color="inherit"
              fontSize={Dimensions[3]}
              titleAccess={undefined}
              className="fs-filter__icon"
            />
          </span>
        </button>
      ) : null}
    </div>
  );
};

const triggerStyle = tsv({
  base: "fs-filter fs-filter-chip__trigger focusable interactive",
  variants: {
    active: {
      true: "fs-filter--active",
    },
    applied: {
      true: "fs-filter--applied fs-filter-chip__trigger--applied",
    },
  },
});

const clearTriggerStyle = tsv({
  base: "fs-filter fs-filter--applied fs-filter-chip__clear-trigger focusable interactive",
});
